// The email domains that attribute inbound mail to this organization.
//
// WHAT THIS DECIDES. When mail arrives from an address this app has never seen,
// `resolveCustomer` matches its domain against this table to work out whose account the
// ticket belongs to. A domain missing here means a new ticket with no organization on it;
// a domain in the wrong account means a ticket filed against the wrong customer, which is
// worse, because it looks right.
//
// A DOMAIN BELONGS TO ONE ORGANIZATION, database-wide, and the server enforces it by
// name: the refusal says which account already holds it, because that is the only thing
// the admin can act on.
//
// REPLACED WHOLESALE, unlike stores beside it. `organization_domains` is a pure lookup
// with nothing referencing it, and the whole set is the unit an admin thinks in — adding
// one domain and removing another is one decision about who a mailbox belongs to, and
// there is no order of two requests that does not pass through a state where mail is
// attributed wrongly.
//
// PUBLIC DOMAINS ARE THE TRAP. `gmail.com` on one account routes every Gmail customer to
// it. The importer has a denylist for exactly this (`PUBLIC_EMAIL_DOMAINS`), and this
// screen warns rather than refuses: a small account really can be all on one domain, and
// the admin is the one who knows.

import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';

/**
 * The same list `src/migration/map.js` refuses at import time. Not imported from there:
 * that module is server-side and this is a warning rather than the rule, so a copy that
 * drifts costs a missing warning and never a wrong write — the server is what decides.
 */
const PUBLIC_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'me.com', 'live.com', 'msn.com', 'comcast.net', 'sbcglobal.net',
]);

/** One per line is how an admin reads a list of domains, and how they paste one. */
const toText = (domains) => (domains ?? []).join('\n');
const toList = (text) => String(text ?? '').split(/[\s,;]+/).filter(Boolean);

export default function OrgDomains({ organizationId }) {
  const [saved, setSaved] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const base = `/admin/organizations/${encodeURIComponent(organizationId)}/domains`;

  const load = useCallback((signal) => apiFetch(base, { signal }).then((body) => {
    setSaved(body.data);
    setText(toText(body.data));
  }), [base]);

  useEffect(() => {
    const controller = new AbortController();
    setSaved(null);
    setError(null);
    load(controller.signal)
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [load]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // The server normalizes — lowercases, trims, strips an `@` prefix, deduplicates —
      // and answers with what it stored, so the box is reseeded from that rather than
      // from what was typed. Otherwise "Bluebird.COM" would sit there looking saved.
      const body = await apiFetch(base, { method: 'PUT', body: { domains: toList(text) } });
      setSaved(body.data);
      setText(toText(body.data));
    } catch (err) {
      setError(consoleErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const draft = toList(text).map((d) => d.trim().toLowerCase());
  const dirty = saved !== null && toText(saved) !== toText(draft);
  const risky = draft.filter((d) => PUBLIC_DOMAINS.has(d));

  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Email domains</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Mail from an unrecognised address is attributed to this organization when its
        domain is listed here. One per line. A domain can only belong to one organization.
      </p>

      {error && <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
      {saved === null && !error && <p className="mt-2 text-sm text-ink-muted">Loading…</p>}

      {saved !== null && (
        <>
          <textarea
            className="field-input mt-2 font-mono text-xs"
            rows={4}
            value={text}
            onChange={(event) => setText(event.target.value)}
            aria-label="Email domains, one per line"
          />

          {risky.length > 0 && (
            // A warning, not a refusal. A small account really can be all on one shared
            // domain, and the admin is the one who knows which case this is.
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
              {risky.join(', ')} {risky.length === 1 ? 'is a public mail domain' : 'are public mail domains'}.
              Listing {risky.length === 1 ? 'it' : 'them'} here attributes every ticket from
              that provider to this organization.
            </p>
          )}

          <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
            <button type="button" className="btn-primary" disabled={!dirty || busy} onClick={save}>
              {busy ? 'Saving…' : 'Save domains'}
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={!dirty || busy}
              onClick={() => { setText(toText(saved)); setError(null); }}
            >
              Discard
            </button>
            {dirty && (
              <span className="text-xs text-ink-muted">
                Not saved. Mail is still attributed by the previous list.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
